import { useEffect, useState } from "react";
import {
  mcp as api,
  type McpServerSummary,
  type McpToolSummary,
} from "../api";

export function Integrations() {
  const [servers, setServers] = useState<McpServerSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .list()
      .then((d) => {
        if (!cancelled) setServers(d.servers);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Integrations</span>
          <h1>MCP servers</h1>
        </div>
      </header>

      <p className="muted">
        These are the Model Context Protocol servers Andersen exposes. Point your
        AI agent (e.g. Quantfolio Platform) at the URL below to call these tools.
      </p>

      {error && <div className="banner-error">{error}</div>}

      {!servers ? (
        <div className="muted">Loading…</div>
      ) : servers.length === 0 ? (
        <div className="empty">
          <p>No MCP servers registered yet.</p>
        </div>
      ) : (
        <div className="mcp-list">
          {servers.map((s) => (
            <McpServerCard key={s.id} server={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function McpServerCard({ server }: { server: McpServerSummary }) {
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}${server.path}`
      : server.path;

  return (
    <section className="mcp-server">
      <header className="mcp-server-head">
        <div>
          <h2>{server.label}</h2>
          <p className="mcp-server-instructions">{server.instructions}</p>
        </div>
        <span className="pill mcp-public">Public · No auth</span>
      </header>

      <dl className="mcp-meta">
        <Field label="URL" value={url} mono copyable />
        <Field label="Transport" value="Streamable HTTP (JSON-RPC)" />
        <Field label="Protocol" value="MCP 2025-06-18" />
        <Field
          label="Tools"
          value={`${server.tools.length} registered`}
        />
      </dl>

      <h3 className="mcp-section-title">Tools</h3>
      <div className="mcp-tools">
        {server.tools.map((t) => (
          <ToolCard key={t.name} tool={t} />
        ))}
      </div>
    </section>
  );
}

function ToolCard({ tool }: { tool: McpToolSummary }) {
  return (
    <article className="mcp-tool">
      <div className="mcp-tool-head">
        <code className="mcp-tool-name">{tool.name}</code>
        <span className="mcp-tool-title">{tool.title}</span>
      </div>
      <p className="mcp-tool-desc">{tool.description}</p>
      {tool.fields.length > 0 && (
        <table className="mcp-tool-fields">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Required</th>
            </tr>
          </thead>
          <tbody>
            {tool.fields.map((f) => (
              <tr key={f.name}>
                <td>
                  <code>{f.name}</code>
                </td>
                <td className="mono">{f.type}</td>
                <td>{f.required ? "yes" : "no"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
}

function Field({
  label,
  value,
  mono,
  copyable,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop */
    }
  }
  return (
    <div className="info-row">
      <dt>{label}</dt>
      <dd className={mono ? "mono" : undefined}>
        {value}
        {copyable && (
          <button
            type="button"
            className="copy-btn"
            onClick={copy}
            aria-label={`Copy ${label}`}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </dd>
    </div>
  );
}
